/**
 * Poll Plugin Internal Types
 *
 * Plugin-private types for the PollRenderer and PollSceneComponent.
 * Layer 2 of the Poll Game Architecture — keeps plugin concerns separate from
 * the shared infrastructure types in libs/shared/data-access.
 */

import { PollDefinition, PollGameSettings, PollQuestion, PollQuestionTally } from '@kanek/shared-data-access';

// ─── Pixel / 3D Size Constants ────────────────────────────────────────────────

/** Width of the glass tablet HTML overlay in pixels (portrait orientation) */
export const POLL_TABLET_WIDTH_PX = 600;

/** Height of the glass tablet HTML overlay in pixels (portrait orientation) */
export const POLL_TABLET_HEIGHT_PX = 800;

/** Depth (thickness) of the 3D glass tablet box geometry in world units */
export const POLL_TABLET_DEPTH = 0.5;

/** Border pixel width of the neon frame around the tablet HTML area */
export const POLL_TABLET_BORDER_PX = 4;

// ─── Renderer Config ──────────────────────────────────────────────────────────

/**
 * Configuration object accepted by PollRenderer.updateConfig().
 * Follows the same pattern as WheelRendererConfig, DiceRollRendererConfig, etc.
 *
 * `items` holds the campaign prizes (for getPrizeIndex mapping at game completion).
 * The poll questions/options come from `settings._previewPollDefinition` (preview)
 * or `settings._pollDefinition` (production — pre-fetched by play.component.ts).
 */
export interface PollRendererConfig {
  settings: PollGameSettings;
  items: Array<{
    id?: string;
    name: string;
    type?: string;
    probability?: number;
    [key: string]: unknown;
  }>;
}

// ─── Game State ───────────────────────────────────────────────────────────────

/**
 * Snapshot of the renderer's current game state, pushed to the scene component
 * via `scene.userData['setPollGameState']` so it can update its signals reactively.
 *
 * The scene never owns this state — the renderer is the single source of truth.
 */
export interface PollGameState {
  /** Current phase of the game */
  mode: 'idle' | 'question' | 'results' | 'complete';

  /** Zero-based index of the question currently shown */
  currentQuestionIndex: number;

  /** Total number of questions in this session */
  totalQuestions: number;

  /** All questions for this session (may be a subset if questionsPerSession is set) */
  questions: PollQuestion[];

  /** Full poll definition (for displaying question/option text) */
  pollDefinition: PollDefinition | null;

  /** Option IDs the user has currently selected for the current question */
  userSelectedOptionIds: string[];

  /**
   * Aggregate tally for the current question (populated in 'results' mode only).
   * Shown as a bar chart in the HTML overlay.
   */
  questionTally?: PollQuestionTally;

  /** When true, the scene shows a visual hint prompting the user to pick an option */
  showValidationHint: boolean;
}
